/**
 * SRPs Vuex module for Portal Application
 * Controls the data and actions for SRPs
 * 
 * @module Srps
 * @see
 * @author Bailey Kulman (Fresche Solutions)
 * @date April 2025
 */

import portalapi from "@services/portalapi";
import { resetStateData } from "@/utils/vuexStoreUtils";
import { getField, updateField } from "vuex-map-fields";

const DEFAULT_STATE = {
    srpClasses: [],
    selectedSrpClass: {},
    selectedInvoiceSrps: {},
};

const state = structuredClone(DEFAULT_STATE);

const getters = {
    getField, // for vuex-map-fields

    getSrpClasses(st) {
        return st.srpClasses;
    },

    getSelectedSrpClass(st) {
        return st.selectedSrpClass;
    },

    getSelectedInvoiceSrps(st) {
        return st.selectedInvoiceSrps;
    },
};

const mutations = {
    updateField, // for vuex-map-fields

    async clearStateData(st) {
        st = resetStateData(st, DEFAULT_STATE);
    },

    setSrpClasses(st, payload) {
        st.srpClasses = payload;
    },

    setSelectedSrpClass(st, payload) {
        st.selectedSrpClass = payload;
    },

    setSelectedInvoiceSrps(st, payload) {
        st.selectedInvoiceSrps = payload;
    },
};

const actions = {

    // Get all SRP classes
    async fetchSrpClasses({ commit }, payload) 
    {
        const requestPayload = {
            page: (payload && payload.page ? payload.page : 1),
            itemsPerPage: (payload && payload.itemsPerPage ? payload.itemsPerPage : 10),
            filters: (payload && payload.filters ? payload.filters : []),
            sortBy: (payload && payload.sortBy ? payload.sortBy : {column: "classCode", state: "asc"}),
        };

        const response = await portalapi.moduleSrps.getSrpClasses(requestPayload);
        
        if (response.status === vm.config.globalProperties.$constants.HTTP_STATUS_CODES.OK) {
            commit("setSrpClasses", response.data);
        }
    },


    // Get SRP class items and data
    async fetchSelectedSrpClass({ commit }, classCode)
    {
        const response = await portalapi.moduleSrps.getSrpClassItems(classCode);
        
        if (response.status === vm.config.globalProperties.$constants.HTTP_STATUS_CODES.OK) {
            commit("setSelectedSrpClass", response.data);
        }
    },

    
    // Get SRP data for invoice items
    async fetchSelectedInvoiceSrps({ commit }, payload)
    {
        const response = await portalapi.moduleSrps.getInvoiceSrpItems(payload);

        if (response.status === vm.config.globalProperties.$constants.HTTP_STATUS_CODES.OK) {
            commit("setSelectedInvoiceSrps", response.data);
        }
    },


    // Update SRPs
    async updateSrps({ commit }, payload)
    {
        const response = await portalapi.moduleSrps.updateSrps(payload);

        if (response.status === vm.config.globalProperties.$constants.HTTP_STATUS_CODES.OK) {
            return response.data;
        } else if (response.status === vm.config.globalProperties.$constants.HTTP_STATUS_CODES.BAD_REQUEST) {
            return response.response.data;
        }

        return;
    },


    // Calculate SRP Gross Margin
    async calculateSrpGrossMargin({ commit }, payload)
    {
        let gmPercent = null;

        if (payload.priceUom !== 'Ea' && payload.srpUom !== null && payload.priceUom !== payload.srpUom && payload.srpMultiplier !== null) {
            payload.price = payload.price * payload.srpMultiplier;
        }

        if (payload.srp !== null && payload.srp !== '' && payload.srp !== 0) {
            gmPercent = parseFloat(((payload.srp - payload.price) / payload.srp) * 100).toFixed(2);
        }

        return gmPercent;
    },
};

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions,
};
